import { DatePipe } from '@angular/common';
import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { of, switchMap } from 'rxjs';
import { LeadFormFieldsComponent } from '../../../shared/components/lead-form-fields/lead-form-fields.component';
import { LeadImportModalComponent } from '../../../shared/components/lead-import-modal/lead-import-modal.component';
import { LeadDuplicatesModalComponent } from '../../../shared/components/lead-duplicates-modal/lead-duplicates-modal.component';
import { LeadMergeModalComponent } from '../../../shared/components/lead-merge-modal/lead-merge-modal.component';
import { LeadMessagePreviewComponent } from '../../../shared/components/lead-message-preview/lead-message-preview.component';
import { LeadProfileAvatarComponent } from '../../../shared/components/lead-profile-avatar/lead-profile-avatar.component';
import { CrmListFiltersDropdownComponent } from '../../../shared/components/crm-list-filters-dropdown/crm-list-filters-dropdown.component';
import type { CrmListFilterOption } from '../../../shared/components/crm-list-filters-dropdown/crm-list-filters-dropdown.model';
import { indianStateOptions } from '../../../shared/constants/indian-states.constants';
import { passportStatusOptions } from '../../../shared/constants/passport-status.constants';
import { LEAD_STREAM_OPTIONS } from '../../../shared/constants/lead-stream.constants';
import {
  emptyLeadForm,
  hasLeadFormFieldErrors,
  leadFormToCreatePayload,
  extractLeadFormApiErrors,
  validateRequiredLeadFormFields,
  type LeadFormFieldErrors,
  type LeadFormFields,
} from '../../../shared/models/lead-form.model';
import { CountriesService } from '../../../shared/services/countries.service';
import { LeadDto, LeadStatusOption } from '../../../shared/services/leads.service';
import { TenantLeadsService } from '../../../shared/services/tenant-leads.service';
import { TenantUsersService } from '../../../shared/services/tenant-users.service';
import {
  AdminButtonComponent,
  AdminConfirmDialogService,
  AdminEmptyStateComponent,
  AdminSidePanelComponent,
  AdminTableComponent,
} from '../../../shared/admin';
import { downloadLeadsExcel } from '../../../shared/utils/lead-export.util';
import {
  duplicateLeadConfirmMessage,
  getDuplicateLeadFromError,
  type ExistingLeadSummary,
} from '../../../shared/utils/lead-duplicate.util';

const ASSIGNED_TO_ME_STORAGE_KEY = 'careergrad.leads.filter.assignedToMe.tenant';
const SHOW_ENROLLED_STORAGE_KEY = 'careergrad.leads.filter.showEnrolled.tenant';
const SHOW_DISQUALIFIED_STORAGE_KEY = 'careergrad.leads.filter.showDisqualified.tenant';

@Component({
  selector: 'app-tenant-leads-page',
  standalone: true,
  imports: [
    DatePipe,
    FormsModule,
    RouterLink,
    AdminTableComponent,
    AdminEmptyStateComponent,
    AdminButtonComponent,
    AdminSidePanelComponent,
    LeadFormFieldsComponent,
    LeadImportModalComponent,
    LeadDuplicatesModalComponent,
    LeadMergeModalComponent,
    LeadMessagePreviewComponent,
    LeadProfileAvatarComponent,
    CrmListFiltersDropdownComponent,
  ],
  template: `
    <section class="leads-page">
      <div class="leads-toolbar">
        <div class="leads-filters">
          <label class="cg-admin-field leads-filters__field">
            <span class="cg-admin-field__label">Search</span>
            <input
              class="cg-input"
              type="search"
              [(ngModel)]="searchInput"
              (ngModelChange)="onSearchChange()"
              name="search"
              placeholder="Name, email, phone"
            />
          </label>
          <app-crm-list-filters-dropdown
            [assignedToValue]="ownerUserId"
            [statusValue]="statusId"
            [destinationValue]="preferredDestination"
            [assignedToOptions]="assignedToFilterOptions()"
            [statusOptions]="statusFilterOptions()"
            [destinationOptions]="destinationFilterOptions()"
            (assignedToChange)="onAssignedToFilterChange($event)"
            (statusChange)="onStatusFilterChange($event)"
            (destinationChange)="onDestinationFilterChange($event)"
            (cleared)="onFiltersClear()"
          />
          <label class="cg-admin-field leads-filters__field">
            <span class="cg-admin-field__label">Stream</span>
            <select class="cg-select" [(ngModel)]="streamFilter" (ngModelChange)="onFilterChange()" name="stream">
              <option value="">All</option>
              @for (s of streamOptions; track s) {
                <option [value]="s">{{ s }}</option>
              }
            </select>
          </label>
          <label class="cg-admin-field leads-filters__field leads-filters__field--date">
            <span class="cg-admin-field__label">From</span>
            <input class="cg-input" type="date" [(ngModel)]="createdFrom" (ngModelChange)="onFilterChange()" name="from" />
          </label>
          <label class="cg-admin-field leads-filters__field leads-filters__field--date">
            <span class="cg-admin-field__label">To</span>
            <input class="cg-input" type="date" [(ngModel)]="createdTo" (ngModelChange)="onFilterChange()" name="to" />
          </label>
        </div>
        <div class="leads-toolbar__actions">
          <div class="leads-toolbar__buttons">
            <app-admin-button variant="primary" type="button" (click)="openImport()">Import Leads</app-admin-button>
            <app-admin-button variant="secondary" type="button" [disabled]="loading() || exporting()" (click)="exportLeads()">
              {{ exporting() ? 'Exporting…' : 'Export' }}
            </app-admin-button>
            <app-admin-button variant="secondary" type="button" [disabled]="loading()" (click)="openDuplicates()">
              Remove Duplicates
            </app-admin-button>
            <app-admin-button variant="primary" type="button" (click)="openCreate()">Create Lead</app-admin-button>
          </div>
          <div class="leads-toolbar__toggles">
            <label class="leads-toolbar__archive">
              <input
                type="checkbox"
                [ngModel]="ownerUserId === 'me'"
                (ngModelChange)="onAssignedToMeToggle($event)"
                name="assignedToMe"
              />
              Show leads assigned to me
            </label>
            <label class="leads-toolbar__archive">
              <input type="checkbox" [(ngModel)]="showArchived" (ngModelChange)="onFilterChange()" name="showArchived" />
              Show archived
            </label>
            <label class="leads-toolbar__archive">
              <input
                type="checkbox"
                [(ngModel)]="showEnrolled"
                (ngModelChange)="onFilterChange()"
                name="showEnrolled"
              />
              Show Enrolled Leads
            </label>
            <label class="leads-toolbar__archive">
              <input
                type="checkbox"
                [(ngModel)]="showDisqualified"
                (ngModelChange)="onFilterChange()"
                name="showDisqualified"
              />
              Show Disqualified Leads
            </label>
          </div>
        </div>
      </div>

      <app-admin-table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Contact</th>
            <th>Assigned to</th>
            <th>Created</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          @if (loading()) {
            <tr>
              <td colspan="5"><p class="cg-form-help">Loading leads…</p></td>
            </tr>
          } @else if (leads().length === 0) {
            <tr>
              <td colspan="5">
                <app-admin-empty-state icon="users" title="No leads" />
              </td>
            </tr>
          } @else {
            @for (lead of leads(); track lead._id) {
              <tr [class.leads-row--archived]="lead.isArchived">
                <td>
                  <a class="cg-admin-link" [routerLink]="['/tenant/leads', lead._id]"><strong>{{ lead.fullName }}</strong></a>
                  @if (lead.subStatus?.name; as subStatusName) {
                    <div class="leads-name-substatus"><strong>{{ subStatusName }}</strong></div>
                  }
                  @if (lead.latestNote || lead.message; as notePreview) {
                    <app-lead-message-preview
                      [message]="notePreview"
                      [modalTitle]="'Note — ' + lead.fullName"
                    />
                  }
                </td>
                <td>
                  <div>{{ lead.email }}</div>
                  <div class="cg-form-help">{{ lead.phoneNumber }}</div>
                </td>
                <td>{{ lead.owner?.displayName || '—' }}</td>
                <td>{{ lead.createdAt | date: 'medium' }}</td>
                <td>{{ lead.status.name }}</td>
              </tr>
            }
          }
        </tbody>
      </app-admin-table>

      @if (!loading()) {
        <div class="leads-pagination">
          <span class="cg-form-help">{{ pageSummary() }}</span>
          <div class="leads-pagination__actions">
            <label class="leads-pagination__page-size">
              <span class="cg-form-help">Rows</span>
              <select class="cg-select" [(ngModel)]="pageSize" (ngModelChange)="onFilterChange()" name="pageSize">
                @for (n of pageSizeOptions; track n) {
                  <option [value]="n">{{ n }}</option>
                }
              </select>
            </label>
            <app-admin-button variant="secondary" type="button" [disabled]="page() <= 1 || loading()" (click)="goToPage(page() - 1)">
              Previous
            </app-admin-button>
            <app-admin-button variant="secondary" type="button" [disabled]="!hasNextPage() || loading()" (click)="goToPage(page() + 1)">
              Next
            </app-admin-button>
          </div>
        </div>
      }
    </section>

    @if (createOpen()) {
    <app-admin-side-panel
      title="Create Lead"
      (closed)="closeCreate()"
    >
      <form class="leads-create-form" (ngSubmit)="submitCreate()">
        <div class="leads-create-form__photo">
          <app-lead-profile-avatar
            [photoUrl]="photoPreviewUrl() ?? undefined"
            [fullName]="createForm.fullName"
            [uploading]="photoUploading()"
            [showRemoveLink]="!!photoPreviewUrl()"
            hint="Passport photo · 3″ × 4″ · max 5 MB"
            (photoSelected)="onCreatePhotoSelected($event)"
            (removePhoto)="removeCreatePhoto()"
          />
        </div>

        <app-lead-form-fields
          [form]="createForm"
          [fieldErrors]="createFieldErrors()"
          [destinationOptions]="destinationOptions()"
          [stateOptions]="stateOptions()"
          [passportStatusOptions]="passportStatusOptionList()"
        />

        @if (createError()) {
          <p class="cg-form-help cg-form-help--error" role="alert">{{ createError() }}</p>
        }
        <div class="leads-create-form__actions">
          <app-admin-button variant="secondary" type="button" [disabled]="createSaving()" (click)="closeCreate()">
            Cancel
          </app-admin-button>
          <app-admin-button variant="primary" type="submit" [disabled]="createSaving() || photoUploading()">
            {{ createSaving() || photoUploading() ? 'Creating…' : 'Create Lead' }}
          </app-admin-button>
        </div>
      </form>
    </app-admin-side-panel>
    }

    @if (importOpen()) {
      <app-lead-import-modal
        area="tenant"
        (closed)="closeImport()"
        (importCompleted)="onImportCompleted()"
      />
    }

    @if (duplicatesOpen()) {
      <app-lead-duplicates-modal
        area="tenant"
        (closed)="closeDuplicates()"
        (removed)="onDuplicatesRemoved()"
        (mergeRequested)="openMerge($event)"
      />
    }

    @if (mergeOpen() && mergeLeadIds().length >= 2) {
      <app-lead-merge-modal
        area="tenant"
        [leadIds]="mergeLeadIds()"
        (closed)="closeMerge()"
        (merged)="onMerged()"
      />
    }
  `,
  styles: `
    .leads-toolbar {
      display: flex;
      flex-direction: column;
      gap: var(--cg-space-3);
      margin-bottom: var(--cg-space-3);
      width: 100%;
      max-width: 100%;
      min-width: 0;
    }
    .leads-filters {
      display: grid;
      grid-template-columns: repeat(5, minmax(0, 1fr));
      column-gap: var(--cg-space-3);
      row-gap: var(--cg-space-2);
      align-items: end;
      width: 100%;
      min-width: 0;
    }
    .leads-filters__field {
      min-width: 0;
    }
    .leads-filters__field .cg-input,
    .leads-filters__field .cg-select {
      width: 100%;
      max-width: 100%;
      min-width: 0;
      box-sizing: border-box;
    }
    .leads-filters__field--date {
      min-width: 0;
      overflow: hidden;
    }
    .leads-filters__field--date .cg-input[type='date'] {
      display: block;
      width: 100%;
      min-width: 0;
      max-width: 100%;
      box-sizing: border-box;
      padding-inline: 0.65rem 0.45rem;
    }
    .leads-filters__field--date .cg-input[type='date']::-webkit-date-and-time-value {
      min-width: 0;
      text-align: left;
    }
    .leads-toolbar__actions {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: space-between;
      gap: var(--cg-space-3);
      width: 100%;
      min-width: 0;
    }
    .leads-toolbar__buttons {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: flex-start;
      gap: var(--cg-space-2);
      min-width: 0;
    }
    .leads-toolbar__toggles {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: flex-end;
      gap: var(--cg-space-2) var(--cg-space-4);
      min-width: 0;
    }
    .leads-toolbar__archive {
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
      font-size: 0.875rem;
      color: var(--cg-color-text-secondary);
      white-space: nowrap;
      cursor: pointer;
    }
    .leads-row--archived { opacity: 0.65; }
    .leads-name-substatus {
      margin-top: 0.2rem;
      font-size: 0.8125rem;
      color: #0f172a;
      line-height: 1.3;
    }
    .leads-pagination {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: space-between;
      gap: var(--cg-space-2);
      margin-top: var(--cg-space-3);
    }
    .leads-pagination__actions {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: var(--cg-space-2);
    }
    .leads-pagination__page-size {
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
      margin: 0;
    }
    .leads-pagination__page-size .cg-select {
      min-width: 4.5rem;
      padding-block: 0.25rem;
    }
    .leads-create-form {
      display: flex;
      flex-direction: column;
      gap: var(--cg-space-2);
    }
    .leads-create-form__photo {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-bottom: 0.5rem;
      padding-bottom: 1rem;
      border-bottom: 1px solid var(--cg-color-border);
    }
    .leads-create-form__actions {
      display: flex;
      flex-wrap: wrap;
      justify-content: flex-end;
      gap: var(--cg-space-2);
      margin-top: var(--cg-space-2);
    }
    @media (max-width: 1439px) {
      .leads-filters {
        grid-template-columns: repeat(3, minmax(0, 1fr));
      }
    }
    @media (max-width: 1023px) {
      .leads-filters {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
      .leads-toolbar__actions {
        flex-direction: column;
        align-items: stretch;
      }
      .leads-toolbar__buttons,
      .leads-toolbar__toggles {
        width: 100%;
        justify-content: center;
      }
    }
    @media (max-width: 639px) {
      .leads-filters {
        grid-template-columns: 1fr;
      }
      .leads-toolbar__buttons > * {
        flex: 1 1 calc(50% - 0.25rem);
      }
      .leads-toolbar__toggles {
        flex-direction: column;
        align-items: flex-start;
        gap: var(--cg-space-2);
      }
    }
  `,
})
export class TenantLeadsPageComponent implements OnInit, OnDestroy {
  private readonly tenantLeadsService = inject(TenantLeadsService);
  private readonly countriesService = inject(CountriesService);
  private readonly tenantUsersService = inject(TenantUsersService);
  private readonly confirmDialog = inject(AdminConfirmDialogService);
  private readonly router = inject(Router);
  private searchTimer: ReturnType<typeof setTimeout> | undefined;

  readonly leads = signal<LeadDto[]>([]);
  readonly statuses = signal<LeadStatusOption[]>([]);
  readonly assignableUsers = signal<Array<{ userId: string; name: string }>>([]);
  readonly loading = signal(false);
  readonly total = signal(0);
  readonly page = signal(1);
  readonly pageSizeOptions = [10, 20, 50];
  readonly createOpen = signal(false);
  readonly importOpen = signal(false);
  readonly duplicatesOpen = signal(false);
  readonly mergeOpen = signal(false);
  readonly mergeLeadIds = signal<string[]>([]);
  readonly createSaving = signal(false);
  readonly createError = signal<string | null>(null);
  readonly createFieldErrors = signal<LeadFormFieldErrors>({});
  readonly destinationOptions = signal<string[]>([]);
  readonly stateOptions = signal<string[]>(indianStateOptions());
  readonly passportStatusOptionList = signal<string[]>(passportStatusOptions());
  readonly streamOptions = LEAD_STREAM_OPTIONS;
  readonly photoUploading = signal(false);
  readonly photoPreviewUrl = signal<string | null>(null);
  readonly exporting = signal(false);

  searchInput = '';
  statusId = '';
  preferredDestination = '';
  streamFilter = '';
  createdFrom = '';
  createdTo = '';
  showArchived = false;
  ownerUserId = '';
  showEnrolled = false;
  showDisqualified = false;
  pageSize = 20;
  createForm: LeadFormFields = emptyLeadForm();
  private pendingPhotoFile: File | null = null;

  readonly hasNextPage = computed(() => this.page() * this.pageSize < this.total());
  readonly pageSummary = computed(() => {
    const total = this.total();
    if (total === 0) return '0 of 0';
    const start = (this.page() - 1) * this.pageSize + 1;
    const end = Math.min(this.page() * this.pageSize, total);
    return `${start}–${end} of ${total}`;
  });
  readonly assignedToFilterOptions = computed<CrmListFilterOption[]>(() => [
    { value: 'me', label: 'Assigned to me' },
    ...this.assignableUsers().map((u) => ({ value: u.userId, label: u.name })),
  ]);
  readonly statusFilterOptions = computed<CrmListFilterOption[]>(() =>
    this.statuses().map((s) => ({ value: s._id, label: s.name })),
  );
  readonly destinationFilterOptions = computed<CrmListFilterOption[]>(() =>
    this.destinationOptions().map((d) => ({ value: d, label: d })),
  );

  ngOnInit(): void {
    this.ownerUserId = this.readAssignedToMePreference() ? 'me' : '';
    this.showEnrolled = this.readShowEnrolledPreference();
    this.showDisqualified = this.readShowDisqualifiedPreference();
    this.loadDestinationOptions();
    this.loadAssignableUsers();
    this.tenantLeadsService.listStatuses().subscribe({
      next: (items) => this.statuses.set(items),
    });
    this.refresh();
  }

  ngOnDestroy(): void {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.revokePhotoPreview();
  }

  onSearchChange(): void {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      this.page.set(1);
      this.refresh();
    }, 300);
  }

  onFilterChange(): void {
    this.persistAssignedToMePreference();
    this.persistShowEnrolledPreference();
    this.persistShowDisqualifiedPreference();
    this.page.set(1);
    this.refresh();
  }

  onAssignedToFilterChange(value: string): void {
    this.ownerUserId = value;
    this.onFilterChange();
  }

  onStatusFilterChange(value: string): void {
    this.statusId = value;
    this.onFilterChange();
  }

  onDestinationFilterChange(value: string): void {
    this.preferredDestination = value;
    this.onFilterChange();
  }

  onFiltersClear(): void {
    this.ownerUserId = '';
    this.statusId = '';
    this.preferredDestination = '';
    this.onFilterChange();
  }

  onAssignedToMeToggle(checked: boolean): void {
    this.ownerUserId = checked ? 'me' : '';
    this.onFilterChange();
  }

  goToPage(next: number): void {
    this.page.set(next);
    this.refresh();
  }

  exportLeads(): void {
    if (this.exporting()) return;
    this.exporting.set(true);
    this.tenantLeadsService
      .exportLeads({
        search: this.searchInput,
        statusId: this.statusId || undefined,
        preferredDestination: this.preferredDestination || undefined,
        stream: this.streamFilter || undefined,
        createdFrom: this.createdFrom || undefined,
        createdTo: this.createdTo || undefined,
        archiveFilter: this.showArchived ? 'archived' : 'active',
        ownerUserId: this.ownerUserId || undefined,
        showEnrolled: this.showEnrolled || undefined,
        showDisqualified: this.showDisqualified || undefined,
      })
      .subscribe({
        next: (rows) => {
          if (rows.length === 0) {
            this.exporting.set(false);
            return;
          }
          const stamp = new Date().toISOString().slice(0, 10);
          downloadLeadsExcel(rows, `leads-export-${stamp}.xlsx`);
          this.exporting.set(false);
        },
        error: () => this.exporting.set(false),
      });
  }

  openCreate(): void {
    this.createForm = emptyLeadForm();
    this.pendingPhotoFile = null;
    this.revokePhotoPreview();
    this.stateOptions.set(indianStateOptions());
    this.passportStatusOptionList.set(passportStatusOptions());
    this.loadDestinationOptions();
    this.createError.set(null);
    this.createFieldErrors.set({});
    this.createOpen.set(true);
  }

  closeCreate(): void {
    this.createOpen.set(false);
    this.createError.set(null);
    this.createFieldErrors.set({});
    this.pendingPhotoFile = null;
    this.revokePhotoPreview();
  }

  openImport(): void {
    this.importOpen.set(true);
  }

  closeImport(): void {
    this.importOpen.set(false);
  }

  onImportCompleted(): void {
    this.closeImport();
    this.refresh();
  }

  openDuplicates(): void {
    this.duplicatesOpen.set(true);
  }

  closeDuplicates(): void {
    this.duplicatesOpen.set(false);
  }

  onDuplicatesRemoved(): void {
    this.refresh();
  }

  openMerge(leadIds: string[]): void {
    this.mergeLeadIds.set(leadIds);
    this.mergeOpen.set(true);
    this.duplicatesOpen.set(false);
  }

  closeMerge(): void {
    this.mergeOpen.set(false);
    this.mergeLeadIds.set([]);
  }

  onMerged(): void {
    this.closeMerge();
    this.refresh();
  }

  onCreatePhotoSelected(file: File): void {
    this.pendingPhotoFile = file;
    this.revokePhotoPreview();
    this.photoPreviewUrl.set(URL.createObjectURL(file));
  }

  removeCreatePhoto(): void {
    this.pendingPhotoFile = null;
    this.revokePhotoPreview();
  }

  submitCreate(allowDuplicate = false): void {
    this.createError.set(null);
    const fieldErrors = validateRequiredLeadFormFields(this.createForm);
    this.createFieldErrors.set(fieldErrors);
    if (hasLeadFormFieldErrors(fieldErrors)) {
      this.createError.set('Please fix the highlighted fields.');
      return;
    }

    this.createSaving.set(true);

    const payload = {
      ...leadFormToCreatePayload(this.createForm),
      ...(allowDuplicate ? { allowDuplicate: true } : {}),
    };

    this.tenantLeadsService
      .create(payload)
      .pipe(
        switchMap((created) => {
          const file = this.pendingPhotoFile;
          if (!file) return of(created);
          this.photoUploading.set(true);
          return this.tenantLeadsService.uploadProfilePhoto(created._id, file).pipe(
            switchMap((updated) => {
              this.photoUploading.set(false);
              return of(updated);
            }),
          );
        }),
      )
      .subscribe({
        next: (created) => {
          this.createSaving.set(false);
          this.photoUploading.set(false);
          this.createOpen.set(false);
          this.pendingPhotoFile = null;
          this.revokePhotoPreview();
          this.refresh();
          void this.router.navigate(['/tenant/leads', created._id]);
        },
        error: (err) => {
          this.createSaving.set(false);
          this.photoUploading.set(false);
          const existing = getDuplicateLeadFromError(err);
          if (existing && !allowDuplicate) {
            void this.confirmDuplicateCreate(existing);
            return;
          }
          const apiFieldErrors = extractLeadFormApiErrors(err?.error);
          if (hasLeadFormFieldErrors(apiFieldErrors)) {
            this.createFieldErrors.set(apiFieldErrors);
            this.createError.set('Please fix the highlighted fields.');
            return;
          }
          this.createError.set(err?.error?.message ?? 'Could not create lead.');
        },
      });
  }

  private async confirmDuplicateCreate(existing: ExistingLeadSummary): Promise<void> {
    const confirmed = await this.confirmDialog.confirm({
      title: 'Lead already exists',
      message: duplicateLeadConfirmMessage(existing),
      confirmLabel: 'Create anyway',
      cancelLabel: 'Cancel',
    });
    if (confirmed) {
      this.submitCreate(true);
    }
  }

  refresh(): void {
    this.loading.set(true);
    this.tenantLeadsService
      .list({
        page: this.page(),
        pageSize: this.pageSize,
        search: this.searchInput,
        statusId: this.statusId || undefined,
        preferredDestination: this.preferredDestination || undefined,
        stream: this.streamFilter || undefined,
        createdFrom: this.createdFrom || undefined,
        createdTo: this.createdTo || undefined,
        archiveFilter: this.showArchived ? 'archived' : 'active',
        ownerUserId: this.ownerUserId || undefined,
        showEnrolled: this.showEnrolled || undefined,
        showDisqualified: this.showDisqualified || undefined,
      })
      .subscribe({
        next: (result) => {
          this.leads.set(result.items);
          this.total.set(result.total);
          this.page.set(result.page);
          this.loading.set(false);
        },
        error: () => {
          this.leads.set([]);
          this.total.set(0);
          this.loading.set(false);
        },
      });
  }

  private loadDestinationOptions(): void {
    this.countriesService.listPublicCountries().subscribe({
      next: (list) => {
        const names = list.map((c) => c.name?.trim()).filter((v): v is string => Boolean(v));
        this.destinationOptions.set(Array.from(new Set(names)).sort((a, b) => a.localeCompare(b)));
      },
      error: () => this.destinationOptions.set([]),
    });
  }

  private loadAssignableUsers(): void {
    this.tenantUsersService.list().subscribe({
      next: (users) => {
        this.assignableUsers.set(
          users
            .filter((u) => u.isActive)
            .map((u) => ({
              userId: u.userId,
              name: [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.email,
            }))
            .sort((a, b) => a.name.localeCompare(b.name)),
        );
      },
      error: () => this.assignableUsers.set([]),
    });
  }

  private revokePhotoPreview(): void {
    const url = this.photoPreviewUrl();
    if (url) URL.revokeObjectURL(url);
    this.photoPreviewUrl.set(null);
  }

  private readAssignedToMePreference(): boolean {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return false;
    }
    return localStorage.getItem(ASSIGNED_TO_ME_STORAGE_KEY) === 'true';
  }

  private persistAssignedToMePreference(): void {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem(ASSIGNED_TO_ME_STORAGE_KEY, String(this.ownerUserId === 'me'));
  }

  private readShowEnrolledPreference(): boolean {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return false;
    }
    return localStorage.getItem(SHOW_ENROLLED_STORAGE_KEY) === 'true';
  }

  private persistShowEnrolledPreference(): void {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem(SHOW_ENROLLED_STORAGE_KEY, String(this.showEnrolled));
  }

  private readShowDisqualifiedPreference(): boolean {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return false;
    }
    return localStorage.getItem(SHOW_DISQUALIFIED_STORAGE_KEY) === 'true';
  }

  private persistShowDisqualifiedPreference(): void {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem(SHOW_DISQUALIFIED_STORAGE_KEY, String(this.showDisqualified));
  }
}
